import type { ReactNode } from "react";
import type { FileResponse } from "@patent-studio/api-types";

/** A page as every screen names it beside a document: "p.4". */
export function pageReference(page: number): string {
  return `p.${String(page)}`;
}

/**
 * Each page a source cites, once, as "p.4". None for a file with no pages of
 * its own: a Word or text file is read onto one page the reader makes up, so
 * "p.1" would say nothing. A file not on the task keeps its pages.
 */
export function citedPages(
  locations: readonly { page?: number | null }[] | undefined,
  file: Pick<FileResponse, "cites_pages"> | undefined,
): string[] {
  if (file?.cites_pages === false) return [];
  return [
    ...new Set(
      (locations ?? [])
        .map((location) => location.page)
        .filter((page): page is number => page != null)
        .map(pageReference),
    ),
  ];
}

/**
 * The one way into the document drawer, wherever a screen offers it: the
 * Invention page's provenance line, a prior art row, a cited passage on the
 * analysis tab. A text link underlined in its own colour, as v0.28 draws a
 * citation, so opening a document reads the same on every screen.
 */
export function OpenDocument({
  onOpen,
  label,
  children,
}: {
  onOpen: () => void;
  /** What a reader hears where the words alone would not say which document. */
  label?: string;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onOpen}
      aria-label={label}
      className="cursor-pointer underline underline-offset-2 hover:text-foreground"
    >
      {children}
    </button>
  );
}
